import { watch } from 'vue';
import { usePrimeVue } from 'primevue/config';
import { useI18n } from 'vue-i18n';

export function usePrimeVueLocale() {
  const primevue = usePrimeVue();
  const { locale } = useI18n();

  const updatePrimeVueLocale = (currentLocale: string) => {
    if (currentLocale === 'fr') {
      // French translations
      primevue.config.locale = {
        startsWith: 'Commence par',
        contains: 'Contient',
        notContains: 'Ne contient pas',
        endsWith: 'Finit par',
        equals: 'Égal',
        notEquals: 'N\'est pas égal',
        noFilter: 'Aucun filtre',
        lt: 'Moins que',
        lte: 'Moins que ou égal à',
        gt: 'Plus que',
        gte: 'Plus que ou égal à',
        dateIs: 'Date est',
        dateIsNot: 'Date n\'est pas',
        dateBefore: 'Date est avant',
        dateAfter: 'Date est après',
        clear: 'Effacer',
        apply: 'Appliquer',
        matchAll: 'Correspond à tous',
        matchAny: 'Correspond à n\'importe quel',
        addRule: 'Ajouter une règle',
        removeRule: 'Supprimer la règle',
        accept: 'Oui',
        reject: 'Non',
        choose: 'Choisir',
        upload: 'Télécharger',
        cancel: 'Annuler',
        completed: 'Terminé',
        pending: 'En attente',
        fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
        dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
        monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
        chooseYear: 'Choisir l\'année',
        chooseMonth: 'Choisir le mois',
        chooseDate: 'Choisir la date',
        prevDecade: 'Décennie précédente',
        nextDecade: 'Prochaine décennie',
        prevYear: 'Année précédente',
        nextYear: 'Prochaine année',
        prevMonth: 'Mois précédent',
        nextMonth: 'Mois suivant',
        prevHour: 'Heure précédente',
        nextHour: 'Heure suivante',
        prevMinute: 'Minute précédente',
        nextMinute: 'Minute suivante',
        prevSecond: 'Seconde précédente',
        nextSecond: 'Seconde suivante',
        am: 'am',
        pm: 'pm',
        today: 'Aujourd\'hui',
        weekHeader: 'Sem',
        firstDayOfWeek: 1,
        showMonthAfterYear: false,
        dateFormat: 'dd/mm/yy',
        weak: 'Faible',
        medium: 'Moyen',
        strong: 'Fort',
        passwordPrompt: 'Entrez un mot de passe',
        emptyFilterMessage: 'Aucun résultat trouvé',
        searchMessage: '{0} résultats sont disponibles',
        selectionMessage: '{0} éléments sélectionnés',
        emptySelectionMessage: 'Aucun élément sélectionné',
        emptySearchMessage: 'Aucun résultat trouvé',
        emptyMessage: 'Aucune option disponible',
        aria: {
          trueLabel: 'Vrai',
          falseLabel: 'Faux',
          nullLabel: 'Non sélectionné',
          star: '1 étoile',
          stars: '{star} étoiles',
          selectAll: 'Tous les éléments sélectionnés',
          unselectAll: 'Tous les éléments désélectionnés',
          close: 'Fermer',
          previous: 'Précédent',
          next: 'Suivant',
          navigation: 'Navigation',
          scrollTop: 'Défiler vers le haut',
          moveTop: 'Déplacer en haut',
          moveUp: 'Déplacer vers le haut',
          moveDown: 'Déplacer vers le bas',
          moveBottom: 'Déplacer en bas',
          moveToTarget: 'Déplacer vers la cible',
          moveToSource: 'Déplacer vers la source',
          moveAllToTarget: 'Déplacer tout vers la cible',
          moveAllToSource: 'Déplacer tout vers la source',
          pageLabel: '{page}',
          firstPageLabel: 'Première page',
          lastPageLabel: 'Dernière page',
          nextPageLabel: 'Page suivante',
          prevPageLabel: 'Page précédente',
          rowsPerPageLabel: 'Lignes par page',
          jumpToPageDropdownLabel: 'Menu déroulant Aller à la page',
          jumpToPageInputLabel: 'Aller à la page Entrée',
          selectRow: 'Ligne sélectionnée',
          unselectRow: 'Ligne désélectionnée',
          expandRow: 'Ligne développée',
          collapseRow: 'Ligne réduite',
          showFilterMenu: 'Afficher le menu des filtres',
          hideFilterMenu: 'Masquer le menu des filtres',
          filterOperator: 'Opérateur de filtre',
          filterConstraint: 'Contrainte de filtre',
          editRow: 'Modifier la ligne',
          saveEdit: 'Enregistrer la modification',
          cancelEdit: 'Annuler la modification',
          listView: 'Vue liste',
          gridView: 'Vue grille',
          slide: 'Glisser',
          slideNumber: '{slideNumber}',
          zoomImage: 'Agrandir l\'image',
          zoomIn: 'Agrandir',
          zoomOut: 'Réduire',
          rotateRight: 'Rotation droite',
          rotateLeft: 'Rotation gauche'
        }
      };
    } else {
      // English translations
      primevue.config.locale = {
        startsWith: 'Starts with',
        contains: 'Contains',
        notContains: 'Not contains',
        endsWith: 'Ends with',
        equals: 'Equals',
        notEquals: 'Not equals',
        noFilter: 'No Filter',
        lt: 'Less than',
        lte: 'Less than or equal to',
        gt: 'Greater than',
        gte: 'Greater than or equal to',
        dateIs: 'Date is',
        dateIsNot: 'Date is not',
        dateBefore: 'Date is before',
        dateAfter: 'Date is after',
        clear: 'Clear',
        apply: 'Apply',
        matchAll: 'Match All',
        matchAny: 'Match Any',
        addRule: 'Add Rule',
        removeRule: 'Remove Rule',
        accept: 'Yes',
        reject: 'No',
        choose: 'Choose',
        upload: 'Upload',
        cancel: 'Cancel',
        completed: 'Completed',
        pending: 'Pending',
        fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        chooseYear: 'Choose Year',
        chooseMonth: 'Choose Month',
        chooseDate: 'Choose Date',
        prevDecade: 'Previous Decade',
        nextDecade: 'Next Decade',
        prevYear: 'Previous Year',
        nextYear: 'Next Year',
        prevMonth: 'Previous Month',
        nextMonth: 'Next Month',
        prevHour: 'Previous Hour',
        nextHour: 'Next Hour',
        prevMinute: 'Previous Minute',
        nextMinute: 'Next Minute',
        prevSecond: 'Previous Second',
        nextSecond: 'Next Second',
        am: 'am',
        pm: 'pm',
        today: 'Today',
        weekHeader: 'Wk',
        firstDayOfWeek: 0,
        showMonthAfterYear: false,
        dateFormat: 'mm/dd/yy',
        weak: 'Weak',
        medium: 'Medium',
        strong: 'Strong',
        passwordPrompt: 'Enter a password',
        emptyFilterMessage: 'No results found',
        searchMessage: '{0} results are available',
        selectionMessage: '{0} items selected',
        emptySelectionMessage: 'No selected item',
        emptySearchMessage: 'No results found',
        emptyMessage: 'No available options',
        aria: {
          trueLabel: 'True',
          falseLabel: 'False',
          nullLabel: 'Not Selected',
          star: '1 star',
          stars: '{star} stars',
          selectAll: 'All items selected',
          unselectAll: 'All items unselected',
          close: 'Close',
          previous: 'Previous',
          next: 'Next',
          navigation: 'Navigation',
          scrollTop: 'Scroll Top',
          moveTop: 'Move Top',
          moveUp: 'Move Up',
          moveDown: 'Move Down',
          moveBottom: 'Move Bottom',
          moveToTarget: 'Move to Target',
          moveToSource: 'Move to Source',
          moveAllToTarget: 'Move All to Target',
          moveAllToSource: 'Move All to Source',
          pageLabel: '{page}',
          firstPageLabel: 'First Page',
          lastPageLabel: 'Last Page',
          nextPageLabel: 'Next Page',
          prevPageLabel: 'Previous Page',
          rowsPerPageLabel: 'Rows per page',
          jumpToPageDropdownLabel: 'Jump to Page Dropdown',
          jumpToPageInputLabel: 'Jump to Page Input',
          selectRow: 'Row Selected',
          unselectRow: 'Row Unselected',
          expandRow: 'Row Expanded',
          collapseRow: 'Row Collapsed',
          showFilterMenu: 'Show Filter Menu',
          hideFilterMenu: 'Hide Filter Menu',
          filterOperator: 'Filter Operator',
          filterConstraint: 'Filter Constraint',
          editRow: 'Row Edit',
          saveEdit: 'Save Edit',
          cancelEdit: 'Cancel Edit',
          listView: 'List View',
          gridView: 'Grid View',
          slide: 'Slide',
          slideNumber: '{slideNumber}',
          zoomImage: 'Zoom Image',
          zoomIn: 'Zoom In',
          zoomOut: 'Zoom Out',
          rotateRight: 'Rotate Right',
          rotateLeft: 'Rotate Left'
        }
      };
    }
  };

  // Initialize with current locale
  updatePrimeVueLocale(locale.value);

  // Watch for locale changes
  watch(locale, (newLocale) => {
    updatePrimeVueLocale(newLocale);
  });

  return {
    updatePrimeVueLocale
  };
}